import {
  Alert,
  AlertDescription,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { CircleAlertIcon, Link2Off, PencilIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface EditableContentsPanelProps {
  /** Slugs currently tagged in the template. */
  sections: string[];
  /** Whether the template has any markup at all — an empty template is not "misconfigured". */
  hasHtml: boolean;
  /** True in code view: sections are listed for reference but cannot be acted on. */
  disabled: boolean;
  onEdit: (sectionName: string) => void;
  onRemove: (sectionName: string) => void;
  /** Fired with the row under the pointer/focus, or null on leave — drives the preview highlight. */
  onHoverSection?: (sectionName: string | null) => void;
}

export function EditableContentsPanel({
  sections,
  hasHtml,
  disabled,
  onEdit,
  onRemove,
  onHoverSection,
}: EditableContentsPanelProps) {
  const { t } = useTranslation('admin');

  const missingSections = hasHtml && sections.length === 0;

  return (
    <div className="flex min-h-0 flex-col gap-4">
      <h2 className="text-base font-medium text-foreground">
        {t('templatesConfig.editableContents.title')}
      </h2>

      {missingSections && (
        // `icon` is a prop, not a child: children land in the vertical content stack, which is what
        // puts the glyph above the text instead of beside it. The two colour classes cover gaps the
        // `destructive` variant leaves — it paints no background at all, and its description
        // override is `*:`-scoped, so it never reaches a description nested two levels down.
        <Alert
          variant="destructive"
          icon={<CircleAlertIcon />}
          className="border-destructive/20 bg-destructive/10"
        >
          <AlertDescription className="text-destructive">
            {t('templatesConfig.editableContents.noUpdatableSections')}
          </AlertDescription>
        </Alert>
      )}

      {sections.length === 0 && !missingSections && (
        <p className="text-center text-sm text-muted-foreground">
          {t('templatesConfig.editableContents.emptyState')}
        </p>
      )}

      <ul className="flex flex-col gap-2 overflow-auto">
        {sections.map((section) => (
          <li
            key={section}
            className="group flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 transition-colors hover:bg-accent"
            onMouseEnter={() => onHoverSection?.(section)}
            onMouseLeave={() => onHoverSection?.(null)}
            // Focus mirrors hover so the highlight is reachable by keyboard, not pointer-only.
            onFocus={() => onHoverSection?.(section)}
            onBlur={() => onHoverSection?.(null)}
          >
            <span
              className={`truncate font-mono text-sm ${
                disabled ? 'text-muted-foreground' : 'text-foreground'
              }`}
            >
              #{section}
            </span>
            <div className="flex shrink-0 gap-1">
              <Button
                variant="ghost"
                size="icon"
                disabled={disabled}
                aria-label={t('templatesConfig.editableContents.editSection', {
                  sectionName: section,
                })}
                onClick={() => onEdit(section)}
              >
                <PencilIcon className="size-4" />
              </Button>
              {/* Unlink, not a trash: this clears the AI marker and leaves the element in place.
                  The glyph, the label and the success toast all had to move together — a screen
                  reader never sees the icon, so "Remove section" was doing the misleading on its
                  own. Its own provider because this panel is a public export of the feature and a
                  missing ancestor provider throws rather than degrading. */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* A disabled button fires no pointer events, so the wrapper is what the
                        tooltip hangs on — and it only takes the tab stop when the button cannot,
                        otherwise every row would cost two tab stops for one control. */}
                    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- see above */}
                    <span tabIndex={disabled ? 0 : -1}>
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={disabled}
                        aria-label={t('templatesConfig.editableContents.clearMarker', {
                          sectionName: section,
                        })}
                        onClick={() => onRemove(section)}
                      >
                        <Link2Off className="size-4" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('templatesConfig.editableContents.clearMarkerHelp')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
